import React, { FunctionComponent, useCallback, useState, useEffect, useMemo } from 'react';
import { Typography, Tooltip } from '@rbx/ui';
import { useTranslation, useLocalization } from '@rbx/intl';
import { OpenAPI } from '@rbx/docsite-utilities/dist/browser';
import ColoredMethod from './ColoredMethod';
import AuthChip from './AuthChip';
import StabilityChip from './StabilityChip';
import useOperationSummaryStyles from './OperationSummary.styles';
import { useOpenAPIDocument } from '../hooks/useOpenAPIDocument';
import { getFirstBaseUrl, extractDomain } from '../utils/serverUtils';
import { getAuthenticationTypes } from '../utils/getAuthenticationTypes';

const SUMMARY_SENTENCE_LIMIT = 3;
const COPY_DEBOUNCE_TIMEOUT = 1000;

type OperationSummaryProps = {
  operation: OpenAPI.OperationObject;
  method: OpenAPI.HttpMethod;
  path: string;
};

// Add zero-width space (U+200B) after hyphens and slashes to encourage breaking there
const formatPathForWrapping = (path: string): string => {
  return path.replace(/([/-])/g, '$1\u200B');
};

const OperationSummary: FunctionComponent<OperationSummaryProps> = ({
  operation,
  method,
  path,
}) => {
  const { classes } = useOperationSummaryStyles();
  const { translate } = useTranslation();
  const { locale } = useLocalization();
  const document = useOpenAPIDocument();
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const baseUrl = getFirstBaseUrl(operation, document);
  const domain = baseUrl ? extractDomain(baseUrl) : null;

  const authTypes = getAuthenticationTypes(operation, document);
  const stability = operation['x-roblox-stability'];

  const sanitizedSummary = useMemo(() => {
    return operation.summary?.replace(/<br\s*\/?>/g, ' ');
  }, [operation.summary]);

  const truncateSummary = useMemo(() => {
    if (!sanitizedSummary) return null;

    const segmenter = new Intl.Segmenter(locale || 'en', { granularity: 'sentence' });
    const segments = Array.from(segmenter.segment(sanitizedSummary));

    if (segments.length <= SUMMARY_SENTENCE_LIMIT) {
      return { text: sanitizedSummary, isTruncated: false };
    }

    const truncatedSegments = segments.slice(0, SUMMARY_SENTENCE_LIMIT);
    const truncatedStr = truncatedSegments
      .map((s) => s.segment)
      .join('')
      .trimEnd();

    return {
      text: `${truncatedStr}...`,
      isTruncated: true,
    };
  }, [sanitizedSummary, locale]);

  // Prevents the event from bubbling up to cause clicks on the operation accordion
  const handleStopPropagation = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
  };

  const handlePathClick = useCallback(
    async (event: React.MouseEvent) => {
      handleStopPropagation(event);
      if (isCopied) return;

      try {
        const textToCopy = baseUrl ? `${baseUrl}${path}` : path;
        await navigator.clipboard.writeText(textToCopy);
        setIsCopied(true);
      } catch {
        // Copy failed, but we don't show any feedback for errors
      }
    },
    [baseUrl, path, isCopied],
  );

  useEffect(() => {
    if (isCopied) {
      const timer = setTimeout(() => {
        setIsCopied(false);
      }, COPY_DEBOUNCE_TIMEOUT);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isCopied]);

  return (
    <div className={classes.root}>
      <div>
        <ColoredMethod method={method} />
      </div>

      <div className={classes.contentContainer}>
        <div className={classes.summaryAndStability}>
          {truncateSummary && (
            <span className={classes.summary}>
              {truncateSummary.isTruncated && sanitizedSummary ? (
                <Tooltip placement='top' title={sanitizedSummary}>
                  <Typography variant='body2' component='span'>
                    {truncateSummary.text}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography variant='body2' component='span'>
                  {truncateSummary.text}
                </Typography>
              )}
            </span>
          )}
          {stability && <StabilityChip stability={stability} />}
        </div>

        <span className={classes.pathWrapper}>
          <Tooltip
            title={translate('Message.Copied')}
            placement='right'
            arrow
            {...(isCopied ? { open: true } : { open: false })}
            // On overflow, flip towards fallbackss
            PopperProps={{
              modifiers: [
                {
                  name: 'flip',
                  options: {
                    fallbackPlacements: ['left', 'top', 'bottom'],
                    padding: 8,
                  },
                },
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: 'viewport',
                    padding: 8,
                  },
                },
                {
                  name: 'offset',
                  options: {
                    offset: [0, 8],
                  },
                },
              ],
            }}>
            <Typography
              variant='code'
              component='span'
              onClick={(e) => {
                e.stopPropagation();
                handlePathClick(e as React.MouseEvent);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  handlePathClick(e as unknown as React.MouseEvent);
                }
              }}
              classes={{ root: classes.path }}>
              {formatPathForWrapping(path)}
            </Typography>
          </Tooltip>
        </span>

        <div className={classes.domainAndTags}>
          {domain && (
            <Typography variant='body2' component='span' color='secondary'>
              {domain}
            </Typography>
          )}
          <div className={classes.tags}>
            {authTypes.map((authType) => (
              <AuthChip key={authType} authType={authType} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperationSummary;
